// The generated dossier for a lead. Phase A renders it deterministically; the shape is the
// documents.content contract from 02 §4.4 and does not change when the LLM arrives.

import { useQuery } from '@tanstack/react-query';
import type { FindingCategory, FindingState } from '@leadg/shared';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';

export interface DossierContent {
  summary: string;
  priority: string | null;
  opportunity: { level: string; categories: string[]; reason: string };
  owner_block: { name: string | null; role: string | null; direct_phone: string | null };
  legal_block?: {
    legal_name?: string | null;
    legal_form?: string | null;
    eik: string | null;
    registry_dump_date: string | null;
    registration_date?: string | null;
    company_status?: string | null;
    financials?: Array<{
      year: number;
      revenue_bgn: number | null;
      profit_bgn: number | null;
      employees: number | null;
    }>;
  } | null;
  reputation?: {
    rating: number | null;
    review_count: number | null;
    as_of?: string | null;
    band?: string | null;
    opening_hours?: unknown;
    top_reviews?: Array<{
      author: string | null;
      rating: number | null;
      text: string;
      relative_time?: string | null;
      relativeTime?: string | null;
    }>;
  } | null;
  site?: {
    url: string | null;
    screenshot_path: string | null;
    ttfb_ms: number | null;
  };
  categories: Array<{
    category: FindingCategory;
    findings: Array<{
      checkKey: string;
      state: FindingState;
      titleBg: string;
      detailBg: string | null;
      improvementBg: string | null;
      consequenceBg?: string | null;
      evidence?: string | null;
    }>;
  }>;
  branches: Array<{ businessName: string; address: string | null }>;
  call_prep: {
    dead_hours: string | null;
    discovery_questions: string[];
    what_not_to_say: string[];
  };
  data_quality_flags: string[];
  score_breakdown: Array<{ reason: string; points: number }>;
}

export interface CueCardContent {
  owner_line: string;
  phone: string | null;
  priority: string | null;
  why_one_liner: string;
  top_pairs: Array<{ check_key: string; seen: string; costs: string }>;
  never_say: string[];
  next_action: string;
  dead_hours: string | null;
}

export type DocumentKind = 'dossier' | 'proposal' | 'cuecard';

export interface StoredDocument<T> {
  content: T;
  status: 'ok' | 'needs_review';
  needs_review_reason: string | null;
  model: string | null;
  generated_at: string | null;
  /** The model refusal the template stood in for, when there was one (documents.llm_error). */
  llm_error: string | null;
}

const DEMO_DOSSIER: DossierContent = {
  summary:
    'Кабинетът има безупречна репутация в Бургас (4.9 ★ от 48 пациента), но губи между 3 и 5 нови пациента всяка седмица поради счупена форма за контакт на мобилни телефони и липса на автоматичен график.',
  priority: 'High',
  opportunity: {
    level: 'Висока',
    categories: ['capabilities', 'performance'],
    reason: 'Голям интерес на пациенти при неработеща форма за записване',
  },
  owner_block: {
    name: 'д-р Елена Иванова',
    role: 'Управител',
    direct_phone: '+359 888 112 233',
  },
  reputation: {
    rating: 4.9,
    review_count: 48,
    band: 'excellent',
    opening_hours: ['Понеделник – Петък: 09:00 – 18:30'],
    top_reviews: [
      {
        author: 'Мария К.',
        rating: 5,
        text: 'Много внимателен екип и безболезнено лечение.',
        relative_time: 'преди 2 седмици',
      },
    ],
  },
  legal_block: {
    legal_name: 'ДЕНТАЛНО СТУДИО Д-Р ИВАНОВА',
    legal_form: 'EOOD',
    eik: '204112233',
    registry_dump_date: '2026-10-10',
    registration_date: '2016-05-12',
    company_status: 'Развиващ се',
    financials: [
      { year: 2024, revenue_bgn: 485000, profit_bgn: 112000, employees: 6 },
      { year: 2023, revenue_bgn: 410000, profit_bgn: 88000, employees: 5 },
    ],
  },
  categories: [
    {
      category: 'capabilities',
      findings: [
        {
          checkKey: 'online_booking',
          state: 'broken',
          titleBg: 'Бутон за записване води към неработеща форма',
          detailBg: 'Бутонът отваря празен попъп с грешка 404.',
          improvementBg: 'Интеграция на модул за директен час без прекъсване на разговора.',
          consequenceBg: 'Пациент вечер затваря страницата и звъни на друг кабинет.',
          evidence: 'form[action=""] без валиден обработчик',
        },
      ],
    },
    {
      category: 'performance',
      findings: [
        {
          checkKey: 'mobile_speed',
          state: 'subpar',
          titleBg: 'Мобилна скорост под препоръчителната (3.4 сек)',
          detailBg: 'Некомпресирани снимки на кабинета забавят мобилния старт.',
          improvementBg: 'Преминаване към WebP/AVIF формат и бързо кеширане.',
          consequenceBg:
            'Над три секунди на телефон и половината посетители вече са в друг резултат.',
          evidence: '3420 ms до пълен HTML отговор',
        },
      ],
    },
  ],
  branches: [],
  call_prep: {
    dead_hours: '13:30 – 14:30',
    discovery_questions: [
      'Колко често пациентите ви пишат във Facebook или звънят след работно време?',
      'Кой поема обажданията през уикенда, когато кабинетът не работи?',
    ],
    what_not_to_say: [
      'Не наричайте сайта „остарял“ или „счупен“ директно.',
      'Не споменавайте сложни уеб технологии като CDN или DNS.',
    ],
  },
  data_quality_flags: [],
  score_breakdown: [
    { reason: 'Отлична репутация и висок брой оценки (4.9 ★)', points: 25 },
    { reason: 'Идентифициран собственик в Търговския регистър', points: 24 },
    { reason: 'Липса на онлайн резервация', points: 25 },
    { reason: 'Бавна мобилна скорост', points: 18 },
  ],
};

const DEMO_CUECARD: CueCardContent = {
  owner_line: 'д-р Елена Иванова · Управител',
  phone: '+359 888 112 233 (директен) · 056 812 345',
  priority: 'High',
  why_one_liner: 'Губят пациенти вечер поради неработещ бутон за записване на час',
  top_pairs: [
    {
      check_key: 'online_booking',
      seen: 'Бутонът „Запиши час“ дава грешка 404',
      costs: 'Губи поне 3-5 нови пациенти всяка седмица',
    },
    {
      check_key: 'mobile_speed',
      seen: 'Зарежда над 3.4 секунди на телефон',
      costs: 'Повече от 40% от мобилните потребители се отказват',
    },
  ],
  never_say: ['Сайтът ви не струва', 'Трябва ви пълна смяна на фирмата'],
  next_action: 'Предложи 15-минутна демонстрация на живо на интерактивния календар',
  dead_hours: '13:30 – 14:30',
};

function useDocument<T>(leadId: number | null, kind: DocumentKind) {
  return useQuery({
    queryKey: ['document', kind, leadId],
    enabled: leadId !== null,
    queryFn: async (): Promise<StoredDocument<T> | null> => {
      if (!isConfigured || isDemoMode) {
        const demoContent = (kind === 'cuecard' ? DEMO_CUECARD : DEMO_DOSSIER) as unknown as T;
        return {
          content: demoContent,
          status: 'ok',
          needs_review_reason: null,
          model: 'claude-3-7-sonnet',
          generated_at: '2026-09-08T12:00:00Z',
          llm_error: null,
        };
      }

      const { data, error } = await supabase
        .from('documents')
        .select('content, status, needs_review_reason, model, generated_at, llm_error')
        .eq('lead_id', leadId!)
        .eq('kind', kind)
        .eq('lang', 'bg')
        .maybeSingle();

      if (error) throw new Error(error.message);
      if (!data) return null;
      return {
        content: data.content as T,
        status: data.status as 'ok' | 'needs_review',
        needs_review_reason: (data.needs_review_reason as string | null) ?? null,
        model: (data.model as string | null) ?? null,
        generated_at: (data.generated_at as string | null) ?? null,
        llm_error: (data.llm_error as string | null) ?? null,
      };
    },
  });
}

export function useDossier(leadId: number | null) {
  const query = useDocument<DossierContent>(leadId, 'dossier');
  return { ...query, data: query.data?.content ?? null, document: query.data ?? null };
}

export function useCueCard(leadId: number | null) {
  return useDocument<CueCardContent>(leadId, 'cuecard');
}
